import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { feedbackAPI } from '../../services/api';

// Approved-review carousel with public stats. Extracted from Pricing.js so the
// landing page can show the same social proof. Renders nothing until at least
// one review has been approved by the superadmin.
const TestimonialsSection = ({ reduceMotion = false }) => {
  const [realReviews, setRealReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [tenantCount, setTenantCount] = useState(0);
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => typeof document === 'undefined' || !document.hidden);
  const [motionAllowed, setMotionAllowed] = useState(() => !reduceMotion);
  const reviewsScrollRef = useRef(null);
  const reviewsPausedRef = useRef(false);
  const sectionRef = useRef(null);
  const resumeTimerRef = useRef(null);
  const dragCleanupRef = useRef(null);

  // Marketing floor: display at least 17+ facilities to avoid discouraging
  // potential customers when actual usage is still low. Once real traffic
  // exceeds this number, the actual count will be shown automatically.
  // To use the real count: remove this constant and use tenantCount directly.
  const MIN_DISPLAY_TENANTS = 17;
  const displayTenantCount = Math.max(tenantCount, MIN_DISPLAY_TENANTS);

  // Only show reviews approved by superadmin (is_public='yes') — no hardcoded fallback
  const allReviews = useMemo(() => {
    const colors = ['from-teal-500 to-cyan-600', 'from-blue-500 to-indigo-600', 'from-indigo-500 to-blue-600', 'from-pink-500 to-rose-600', 'from-orange-500 to-amber-600', 'from-teal-600 to-emerald-600', 'from-emerald-600 to-teal-600', 'from-purple-500 to-amber-500', 'from-violet-500 to-teal-500', 'from-orange-500 to-indigo-500'];
    return realReviews.map((r, idx) => ({
      name: 'Verified User',
      role: 'Ospito+ User',
      initials: r.reviewer.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'U',
      color: colors[idx % colors.length],
      rating: r.rating,
      quote: r.message,
      reviewer: r.reviewer,
      isReal: true,
    }));
  }, [realReviews]);

  // Compute average rating from approved reviews
  const averageRating = useMemo(() => {
    const rated = allReviews.filter(r => r.rating);
    if (rated.length === 0) return null;
    return (rated.reduce((sum, r) => sum + r.rating, 0) / rated.length).toFixed(1);
  }, [allReviews]);

  // Fetch approved public reviews and public stats
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [res, statsRes] = await Promise.all([
          feedbackAPI.publicReviews(20),
          feedbackAPI.publicStats(),
        ]);
        if (!cancelled) {
          setRealReviews(res.data || []);
          setTenantCount(statsRes.data?.tenant_count || 0);
        }
      } catch {
        // Silently show no reviews
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      setMotionAllowed(false);
      return undefined;
    }
    const media = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    const update = () => setMotionAllowed(!media?.matches);
    update();
    if (media?.addEventListener) media.addEventListener('change', update);
    else media?.addListener?.(update);
    return () => {
      if (media?.removeEventListener) media.removeEventListener('change', update);
      else media?.removeListener?.(update);
    };
  }, [reduceMotion]);

  useEffect(() => {
    if (reviewsLoading || allReviews.length === 0) return undefined;
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsSectionVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]) setIsSectionVisible(entries[0].isIntersecting);
    }, { rootMargin: '150px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [reviewsLoading, allReviews.length]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const update = () => setIsDocumentVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  // JS-driven auto-scroll for reviews carousel.
  // Continuously moves left; chevrons and drag adjust the offset and
  // briefly pause auto-scroll, then it resumes from the new position.
  useEffect(() => {
    if (reviewsLoading || allReviews.length === 0 || !isSectionVisible || !isDocumentVisible || !motionAllowed) return undefined;
    const container = reviewsScrollRef.current;
    const parent = container?.parentElement;
    if (!container || !parent) return undefined;
    let raf = null;
    let lastTimestamp = 0;
    let maxScroll = 0;
    let autoScrollStarted = false;
    let tick;

    const updateMaxScroll = () => {
      // Reviews are rendered once (not duplicated), so the full
      // scrollWidth is the distance to scroll before looping back.
      maxScroll = Math.max(0, container.scrollWidth - parent.clientWidth);
      const current = container._manualOffset || 0;
      if (maxScroll <= 0) {
        container._manualOffset = 0;
        container.style.transform = 'translate3d(0, 0, 0)';
        if (raf !== null) cancelAnimationFrame(raf);
        raf = null;
        return;
      }
      if (current < -maxScroll) {
        container._manualOffset = -maxScroll;
        container.style.transform = `translate3d(${-maxScroll}px, 0, 0)`;
      }
      if (autoScrollStarted && raf === null && !document.hidden) {
        lastTimestamp = 0;
        raf = requestAnimationFrame(tick);
      }
    };
    updateMaxScroll();

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateMaxScroll);
    if (resizeObserver) {
      resizeObserver.observe(container);
      resizeObserver.observe(parent);
    } else {
      window.addEventListener('resize', updateMaxScroll, { passive: true });
    }

    tick = (timestamp) => {
      if (document.hidden || maxScroll <= 0) {
        raf = null;
        return;
      }
      const frameTime = lastTimestamp ? Math.min(timestamp - lastTimestamp, 50) : 1000 / 60;
      lastTimestamp = timestamp;
      if (!reviewsPausedRef.current) {
        let current = (container._manualOffset || 0) - (24 * frameTime / 1000);
        // Loop back to start when we've scrolled past the last card
        if (current <= -maxScroll) current = 0;
        container._manualOffset = current;
        container.style.transform = `translate3d(${current}px, 0, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };

    // Small delay to let DOM render the reviews
    const startTimer = setTimeout(() => {
      autoScrollStarted = true;
      updateMaxScroll();
      if (maxScroll > 0 && raf === null) raf = requestAnimationFrame(tick);
    }, 500);

    return () => {
      clearTimeout(startTimer);
      if (raf !== null) cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMaxScroll);
    };
  }, [reviewsLoading, allReviews.length, isSectionVisible, isDocumentVisible, motionAllowed]);

  useEffect(() => () => {
    dragCleanupRef.current?.();
    clearTimeout(resumeTimerRef.current);
  }, []);

  // Helper: nudge the carousel and briefly pause auto-scroll
  const scheduleResume = (delay) => {
    clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      reviewsPausedRef.current = false;
      resumeTimerRef.current = null;
    }, delay);
  };

  const nudgeReviews = (delta) => {
    const c = reviewsScrollRef.current;
    if (!c) return;
    const maxScroll = Math.max(0, c.scrollWidth - c.parentElement.clientWidth);
    let current = c._manualOffset || 0;
    current += delta;
    // Clamp within scrollable range (no wrap — single set of cards)
    if (current > 0) current = 0;
    if (current < -maxScroll) current = -maxScroll;
    c._manualOffset = current;
    c.style.transform = `translate3d(${current}px, 0, 0)`;
    // Pause auto-scroll briefly, then resume
    reviewsPausedRef.current = true;
    scheduleResume(3000);
  };

  // Memoized reviews — single loop of cards
  const memoizedReviews = useMemo(() => (
    <div className="flex gap-4">
      {allReviews.map((review, idx) => (
          <div key={idx} className="w-72 flex-shrink-0 group relative">
            <div className="relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/50 p-4 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${review.color} flex items-center justify-center text-white font-bold text-xs shadow-sm`}>
                    {review.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{review.reviewer}</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{review.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 24 24"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.383 2.46a1 1 0 00-.364 1.118l1.286 3.966c.3.922-.755 1.688-1.54 1.118l-3.383-2.458a1 1 0 00-1.175 0l-3.383 2.458c-.784.57-1.838-.196-1.539-1.118l1.285-3.966a1 1 0 00-.364-1.118l-3.383-2.46c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.967z" /></svg>
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{review.rating}</span>
                </div>
              </div>
              <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-2 mb-3">
                "{review.quote}"
              </p>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700/50">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{review.name}</span>
                <div className="flex items-center gap-1 text-emerald-500">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  <span className="text-[10px] font-semibold">Verified</span>
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  ), [allReviews]);

  if (reviewsLoading || allReviews.length === 0) return null;

  return (
    <div ref={sectionRef} className="relative py-12 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Compact Header with Stats */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">Verified feedback</p>
            <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">Trusted by <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500 animate-gradient-x">{displayTenantCount}+</span> facilities across the globe</h2>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">Real feedback from healthcare providers running Ospito+ every day</p>
          </div>
          {/* Mini Stats */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-700/30">
              <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 24 24"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.383 2.46a1 1 0 00-.364 1.118l1.286 3.966c.3.922-.755 1.688-1.54 1.118l-3.383-2.458a1 1 0 00-1.175 0l-3.383 2.458c-.784.57-1.838-.196-1.539-1.118l1.285-3.966a1 1 0 00-.364-1.118l-3.383-2.46c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.967z" /></svg>
              <span className="text-sm font-bold text-amber-700 dark:text-amber-400">{averageRating}<span className="text-amber-500/60 dark:text-amber-400/60">/5</span> <span className="text-amber-500/60 dark:text-amber-400/60 font-normal">({allReviews.filter(r => r.rating).length})</span></span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700/50">
              <span className="text-sm">⚡</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">99.9%</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Uptime</span>
            </div>
          </div>
        </div>

        {/* Auto-scrolling Reviews — chevrons outside the scroll area */}
        <div className="flex items-center gap-3">
          {/* Chevron — Previous (go back to cards that passed) */}
          <button
            onClick={() => nudgeReviews(340)}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-teal-500 hover:text-white hover:border-teal-500 transition-all duration-200 hover:scale-110"
            aria-label="Previous reviews"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Scroll Area */}
          <div className="relative min-w-0 overflow-hidden flex-1">
            {/* Fade Edges — match page background for seamless blend */}
            <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white dark:from-gray-900 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white dark:from-gray-900 to-transparent z-10 pointer-events-none"></div>

            <div
              ref={reviewsScrollRef}
              className="flex gap-4 w-max py-2 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onMouseDown={(e) => {
                const container = e.currentTarget;
                const startX = e.pageX - container.offsetLeft;
                const startOffset = container._manualOffset || 0;
                let isDragging = true;
                dragCleanupRef.current?.();
                clearTimeout(resumeTimerRef.current);
                resumeTimerRef.current = null;
                reviewsPausedRef.current = true;

                const onMove = (ev) => {
                  if (!isDragging) return;
                  const x = ev.pageX - container.offsetLeft;
                  const maxScroll = Math.max(0, container.scrollWidth - container.parentElement.clientWidth);
                  let newOffset = startOffset + (x - startX);
                  if (newOffset > 0) newOffset = 0;
                  if (newOffset < -maxScroll) newOffset = -maxScroll;
                  container._manualOffset = newOffset;
                  container.style.transform = `translate3d(${newOffset}px, 0, 0)`;
                };
                const cleanupDrag = () => {
                  if (!isDragging) return;
                  isDragging = false;
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                  if (dragCleanupRef.current === cleanupDrag) dragCleanupRef.current = null;
                };
                const onUp = () => {
                  cleanupDrag();
                  scheduleResume(2000);
                };
                dragCleanupRef.current = cleanupDrag;
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            >
              {memoizedReviews}
            </div>
          </div>

          {/* Chevron — Next (skip ahead) */}
          <button
            onClick={() => nudgeReviews(-340)}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-teal-500 hover:text-white hover:border-teal-500 transition-all duration-200 hover:scale-110"
            aria-label="Next reviews"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

      </div>
    </div>
  );
};

export default TestimonialsSection;
